import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';

import { ROUTES } from '@/utils/routes';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Form } from '@/components/forms/Form';
import { PHARMACOVIGILANCE_PAGE_QUERY } from '@/data/pharmacovigilancePageQuery';
import formDataStatic from '@/data/formData.json';
import { Section } from '@/components/common/Section';
import { Heading } from '@/components/typography';

import Image from 'next/image';
import DecorIcon from '@/public/icons/pharmacovigilance-decor-icon.svg';
import { Hero } from '@/components/common/Hero';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    title,
    featuredImage,
    seo,
    pharmacovigilancePage: { pharmacovigilance },
  } = data?.pageBy?.translation ?? {};
  const featureImage = featuredImage?.node;
  const {
    subtitle,
    description,
    list,
    supervisionSection,
    listSection,
    documents,
  } = pharmacovigilance;
  const { t } = useTranslation('common');
  const documentsTitle = t('documentsTitle');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [language, setLanguage] = useState<'EN' | 'UA'>('UA');

  const router = useRouter();

  useEffect(() => {
    router.locale == 'en' ? setLanguage('EN') : setLanguage('UA');
  }, [router]);

  const formDataValues = formDataStatic[language];

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero breadcrumbs={seo?.breadcrumbs}>
            <div className="relative mb-[60px] pb-7 before:absolute before:bottom-0 before:left-0 before:h-[1px] before:w-full before:bg-primaryLight before:content-[''] dark:text-darkFullLight md:mb-[80px] md:pb-10 lg:mb-[100px] lg:flex lg:items-center lg:justify-between">
              <Heading
                tag="h1"
                content={title || ''}
                className="overflow-wrap mb-5 break-words md:text-h1"
              />
              {formDataValues && (
                <ScrollLink
                  to="formPharmacovigilance"
                  href="/pharmacovigilance"
                  className="flex h-12 max-w-[289px] cursor-pointer items-center justify-center rounded-base border-2 border-accentMain bg-accentMain px-btnX py-btnY text-center text-md font-normal text-white transition-colors hover:border-accentAdditional hover:bg-accentAdditional focus:border-accentAdditional focus:bg-accentAdditional"
                  smooth={true}
                  duration={100}
                  offset={-180}
                >
                  {formDataValues.buttonName}
                </ScrollLink>
              )}
            </div>
            <div className="lg:grid lg:grid-cols-[.8fr,_1fr] lg:gap-[60px]">
              {featureImage && (
                <div className="hidden lg:block">
                  <Image
                    src={featureImage?.mediaItemUrl ?? ''}
                    alt={featureImage?.altText ?? ''}
                    width={featureImage?.mediaDetails?.width}
                    height={featureImage?.mediaDetails?.height}
                    className=""
                  />
                </div>
              )}
              <div
                className=" pl-16 md:pl-[26%] lg:pl-0"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </Hero>
          <Section className="pt-[60px] md:pt-[100px] lg:pt-[100px]">
            <Heading
              content={subtitle}
              color="accent-main"
              className="dark:gray-scale-red mb-7 text-xl md:mb-10 md:text-h3 lg:mb-[60px] lg:max-w-[62%]"
            />

            <ul className="grid md:grid-flow-col md:grid-cols-2 md:grid-rows-3 md:gap-x-8 lg:mx-auto lg:max-w-[85%] ">
              {list.map((item, index) => (
                <li
                  key={item.item}
                  className={`grid grid-cols-[50px,_1fr] gap-x-9 border-b border-primaryLight py-[34px] md:py-10 md:last:border-none ${
                    index % 3 === 2 ? 'md:border-none md:pb-0' : ''
                  }`}
                >
                  <span className="text-xl md:text-h3">
                    {'/0' + (index + 1)}
                  </span>
                  <p className="">{item.item}</p>
                </li>
              ))}
            </ul>
          </Section>
          <Section className="pt-[60px] md:pt-[100px] lg:pt-[100px]">
            <Heading
              content={listSection.title}
              color="accent-main"
              className="dark:gray-scale-red mb-7 text-xl md:mb-10 md:text-h3 lg:mb-[60px] lg:max-w-[62%]"
            />

            <ul className="grid md:grid-flow-col md:grid-cols-2 md:grid-rows-5 md:gap-x-8 lg:mx-auto lg:max-w-[85%] ">
              {listSection.list.map((item, index) => (
                <li
                  key={index}
                  className={`grid grid-cols-[50px,_1fr] gap-x-9 border-b border-primaryLight py-[34px] md:py-10 md:last:border-none ${
                    index % 5 === 4 ? 'md:border-none md:pb-0' : ''
                  }`}
                >
                  <span className="text-xl md:text-h3">
                    {'/0' + (index + 1)}
                  </span>
                  <p className="">{item.item}</p>
                </li>
              ))}
            </ul>
          </Section>
          <Section className="">
            <Heading tag="h2" withDivider content={supervisionSection.title} />
            <div
              className="mb-[60px] pl-16 md:mb-[100px] md:pl-[184px] lg:pl-[416px]"
              dangerouslySetInnerHTML={{
                __html: supervisionSection.description,
              }}
            />
            <div
              id="formPharmacovigilance"
              className="grid gap-8 rounded bg-accentAdditional p-[26px] text-white md:gap-10 md:py-10 md:pl-10 md:pr-[67px] lg:relative lg:gap-[60px] lg:p-[60px] lg:pr-[72px]"
            >
              <p
                className="!mb-0 lg:max-w-[75%]"
                dangerouslySetInnerHTML={{ __html: supervisionSection.appeal }}
              />
              <div className="grid gap-8 md:gap-10 lg:grid-cols-[70%,_1fr] lg:gap-0">
                <DecorIcon className="hide hide-image max-w-[400px] lg:absolute lg:bottom-0 lg:left-0 lg:max-w-[458px]" />
                <div
                  className="justify-self-end lg:col-start-2 lg:justify-self-start"
                  dangerouslySetInnerHTML={{
                    __html: supervisionSection.appealContacts,
                  }}
                />
              </div>
            </div>
          </Section>
          <Section>
            <Form />
          </Section>
          <Section className="!pb-[100px]">
            <Heading
              tag="h2"
              className="mb-6 md:mb-8"
              content={documentsTitle}
              withDivider
            />
            <ul className="grid gap-12 md:gap-7">
              {documents.map((item, index) => (
                <li key={index} className={``}>
                  <Link
                    className="flex items-center gap-4"
                    href={item.file?.mediaItemUrl ?? ''}
                    target="_blank"
                    prefetch={false}
                  >
                    <Image
                      src={'/icons/file.svg'}
                      alt={'Documents icon'}
                      width={80}
                      height={80}
                      className="mb-auto h-[48px] w-[48px] md:h-[64px] md:w-[64px]"
                    />
                    <p className="text-[20px] font-bold text-accentMain dark:text-accentAdditional md:text-[28px] md:leading-[36px]">
                      {item.title}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const uri = ROUTES.PHARMACOVIGILANCE ?? ROUTES.HOME;

  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: PHARMACOVIGILANCE_PAGE_QUERY,
    variables: {
      uri,
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });

  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
